import React from 'react';
import { toast } from 'sonner';

const CRASH_TOAST_ID = 'error:page-crash';

/**
 * Last line of defence for render crashes. Without it one bad value (an
 * object `detail` rendered as text, a missing field) blanks the whole app —
 * or, in development, shows the raw stack. Shows a friendly card + toast
 * instead; the real error goes to the console. `resetKey` (the route path)
 * clears the error when the user navigates elsewhere.
 */
export default class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error, info) {
    console.error('[AppErrorBoundary]', error, info?.componentStack);
    toast.error('Something went wrong on this page.', {
      id: CRASH_TOAST_ID,
      description: 'Try again, or reload the page.',
    });
  }

  componentDidUpdate(prevProps) {
    if (this.state.failed && prevProps.resetKey !== this.props.resetKey) this.setState({ failed: false });
  }

  retry = () => this.setState({ failed: false });

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6" data-testid="app-error-fallback">
        <div className="max-w-sm w-full bg-white rounded-2xl border border-slate-200 p-6 text-center shadow-sm">
          <p className="text-[15px] font-extrabold text-slate-900">Something went wrong</p>
          <p className="text-[13px] text-slate-500 mt-1.5">This page ran into a problem. You can try again, or reload the page.</p>
          <div className="flex justify-center gap-2 mt-5">
            <button onClick={this.retry} className="h-9 px-4 rounded-xl bg-white border border-slate-200 text-[12px] font-bold text-slate-700 hover:bg-slate-50" data-testid="app-error-retry">
              Try again
            </button>
            <button onClick={() => window.location.reload()} className="h-9 px-4 rounded-xl bg-emerald-600 text-white text-[12px] font-bold hover:bg-emerald-700" data-testid="app-error-reload">
              Reload page
            </button>
          </div>
        </div>
      </div>
    );
  }
}
